const changelogMarkdown = `## Unreleased
_No unreleased changes._
## 2.0.2 (2026-05-18)
### Bug Fixes
* expand earlier cycle spoiler tags when selecting or importing future cycles
### Documentation
* clarify beta versioning guidance for release candidates
## 2.0.1 (2026-04-25)
### Bug Fixes
* disable spellcheck, autocorrect, and autocapitalization for searchable dropdown fields on mobile browsers
## 2.0.0 (2026-04-24)
### Features
* overhaul the simulator UI with a new two-workspace shell, shared configuration toolbar, Quick Assist guidance, and a dedicated results stage
* add cancellable simulation runs with loading placeholders that preserve the current results view until new output is ready
* add loadout sharing, saved favorites, shorter shared links, and a smoother import flow for manual and armory loadouts
* add power reroll strategy modes for comparing table heuristics, legacy behavior, and optimal play
* improve the loadout analyzer, mobile chart axes, and compare workflow navigation
### Bug Fixes
* fix Sun Descendant primordial A.T. lookup and composite ATCC titan power imports
* patch ATCC import and card parsing edge cases, including updated card data and hit-gated power dice
* prevent searchable selectors from reopening unexpectedly and fix loadout card resize bounce
* refine histogram spacing, shared import prioritization, and battlefield Kratos token ordering
### Documentation
* preload the changelog for instant rendering and refresh the guide/help copy
* add a fan-run Discord thread link for questions, bug reports, and suggestions
## 1.2.0 (2026-04-19)
### Features
* simulate primordial AT ranges
* add searchable selectors across the app
* bundle ATCC armory data
* streamline loadout naming
* refine weapon compare curves
* integrate weapon compare into simulator
* Hide argonaut token inputs in simple mode
* Apply despair precision penalty and reorder token UI
### Bug Fixes
* correct primordial BP AT imports
* remove welcome screen loading delay
* persist schema reset warning until armory reset
* improve searchable selector dropdown UX
* restore battlefield kratos pool layout
* persist battlefield compare selections
* parse hit-gated ATCC power dice
* handle updated ATCC card data
* Preserve minArgonautTokens in defaultStatsWithRequirements
* Bump schema version to prompt re-sync of ATCC gear data
* Preserve minArgonautTokens through sanitization
### Refactors
* rename simulator mode toggle
### Documentation
* improve welcome screen ATCC credit
* clarify welcome screen armory data sourcing
* refresh welcome screen and simulator typography
### Other
* Add ATCC data fetch/generate utility scripts
* Update changelog and package version
## 1.1.0 (2026-03-08)
### Features
* Add argonaut token gates (Ambrosia, Despair, Bleed, Midas, Pain)
* Add ATCC gear sync integration
* Add ATCC titan sync support
* add Closing token support (Cycle IV)
* Add kratos table and pattern sync from ATCC
* add Mortal power die type
* added a welcome screen
* added all core game titans and their kratos tables
* added armory editor to add/update/delete named titans, weapons, etc.
* added button to copy titan loadout to loadout stats
* added changelog to app ui
* added confirmation popups for file upload and data reset
* added fate re-rolls for titan loadouts
* added kratos tables as an independent item (breaking change: requires users to delete stored data otherwise argonaut loadouts will include kratos tables from titans)
* added loadout naming
* added more stats to graph
* added option to attack with rage + 1
* added power dice limits
* added precision and power bonuses for argonaut loadouts
* added rouse tokens
* added standard tags for each cycle and the theatre
* added tags for items to control what items are visible
* added tooltips for optional simulation configs
* adding auto-tokens for loadout stats
* Apply despair precision penalty and reorder token UI
* changed 'upload' feature to add/update armory items rather than replace them
* changed the style to more closely match ato's color theme
* enable Cycle IV and Cycle V spoiler tags
* handle RedtoBlack die upgrade in kratos table import
* hide loadout custom name field in simple mode
* last loadout can be removed
* map ATCC Mortal die color in card translator
* Move Primordial Calculator into Battlefield section as toggle
* **primordial-calculator:** Add Primordial Calculator tab
* replaced power dice text inputs with dice display and buttons
* some inputs, such as certain kratos tokens, are now hidden behind spoiler tag visibility
* **ui:** overhaul UI with improved layout, typography, and simple/advanced mode
* **ui:** rename Gear to Other Gear, rename Save/Load to Custom Item, center Custom Item card, and improve existing item dropdown UX
* **ui:** replace window.confirm with custom ConfirmModal for mobile support
### Bug Fixes
* +/- buttons now work on initial load
* a 10 always hits on attack roll
* added 0.5 second delay to tooltip hover
* Bump schema version to prompt re-sync of ATCC gear data
* can no longer zoom out too far on mobile
* changelog limit now correctly counts all version headings (## and ###)
* correct ATCC kratos table import issues
* Correct perHitPowerDiceOverride indexing for hits-gated power dice
* correct Warkeeper kratos table RedToBlack die upgrade
* duplicating a loadout then changing it (or adding multiple new loadouts then changing) no longer results in multiple loadouts being modified
* failure to load titan loadouts or loadout stats from storage no longer crashes the app
* fix button hover behavior and sizes
* graph correctly displays bar for last power value
* graph y-axis stops at 100% instead of 105%
* import ATCC cards with ability-only stats like cryptexes
* item precision bonuses no longer double counted
* last loadout stats can be removed
* maxHits starts at 1 instead of 0
* Persist primordial manual/calculator toggle across tab switches
* power re-rolls for titan kratos table now work
* Preserve minArgonautTokens in defaultStatsWithRequirements
* Preserve minArgonautTokens through sanitization
* reduced execution time for simulations with power re-rolls
* reducing minimum graph size for mobile
* Reset Armory only clears armory data, not all localStorage
* reverted fix to execution time with re-rolls as it was causing incorrect results
* show Mortal die in full hit power dice display
* simulating after adding new loadout no longer errors
* titan loadout now validated when loaded and updated instead of just when submitted
* Translate ungated plus-Power entries to powerBonus
* updated config tooltip wording
* updating existing armory item now reflects in the armory loadout without needing to re-select the item
`;

export default changelogMarkdown;
